import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { getPropertyPriceLabel } from './propertyPrice';
import { isLandPropertyType } from './propertyApplicability';

interface Property {
  id: string;
  title: string;
  description?: string;
  type: 'vendita' | 'affitto';
  price: number | null;
  price_on_request?: boolean;
  location: string;
  bedrooms: number;
  bathrooms?: number | null;
  size: number;
  energy_class?: string;
  reference_number?: string;
  total_rooms?: number;
  extra_costs?: string;
  type_id?: string | null;
  property_types?: { id?: string; code?: string | null; name?: string } | null;
  property_states?: { name: string } | null;
  provinces?: { name: string };
  zones?: { name: string };
  property_images?: Array<{
    url: string;
    is_primary: boolean;
  }>;
  property_strengths?: Array<{
    description: string;
  }>;
  property_features?: Array<{
    features: {
      name: string;
    };
  }>;
}

const generatePropertyPDF = async (property: Property, openInNewTab = false) => {
  const isLand = isLandPropertyType(property.property_types || { id: property.type_id });
  const showBathrooms = !isLand || (property.bathrooms ?? 0) > 0;
  const showState = !isLand || Boolean(property.property_states?.name?.trim());
  // Crea un nuovo documento PDF (orientamento portrait, unità mm, formato A4)
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  try {
    // Aggiungi font personalizzato
    doc.setFont('helvetica', 'bold');
    
    // Intestazione
    doc.setTextColor(29, 43, 78); // Colore primario #1D2B4E
    doc.setFontSize(20);
    doc.text('MOLINARO IMMOBILIARE', 105, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Via Pisana 327, 50018 Scandicci (FI)', 105, 20, { align: 'center' });
    doc.text('Tel: +39 327 8855115 | Email: info@molinaroimmobiliare.it', 105, 24, { align: 'center' });
    
    // Linea separatrice
    doc.setDrawColor(29, 43, 78);
    doc.setLineWidth(0.5);
    doc.line(15, 28, 195, 28);
    
    // Titolo immobile
    doc.setFontSize(16);
    doc.setTextColor(29, 43, 78);
    doc.text(property.title, 105, 36, { align: 'center' });
    
    // Riferimento e data stampa
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    
    const today = format(new Date(), 'dd/MM/yyyy');
    doc.text(`Rif. ${property.reference_number || 'N/D'} - Scheda generata il ${today}`, 105, 42, { align: 'center' });
    
    // Immagine principale (se disponibile)
    if (property.property_images && property.property_images.length > 0) {
      const mainImage = property.property_images.find(img => img.is_primary) || property.property_images[0];
      
      try {
        const imgData = await loadImage(mainImage.url);
        doc.addImage(imgData, 'JPEG', 15, 45, 180, 90, undefined, 'FAST');
      } catch (error) {
        console.error('Errore caricamento immagine:', error);
        // In caso di errore, continua senza immagine
      }
    }
    
    // Posizione Y dopo l'immagine
    let yPos = 145;
    
    // Dettagli principali
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(29, 43, 78);
    doc.setFontSize(14);
    doc.text('CARATTERISTICHE PRINCIPALI', 15, yPos);
    
    yPos += 10;
    
    // Colonne presenti solo quando il dato è applicabile e disponibile.
    const mainDetails = [
      ['Tipologia', property.property_types?.name || 'N/D'],
      ...(showState ? [['Stato', property.property_states?.name || 'N/D']] : []),
      ['Prezzo', getPropertyPriceLabel(property)],
      ['Superficie', `${property.size} m²`],
    ];
    (doc as any).autoTable({
      startY: yPos,
      head: [mainDetails.map(([label]) => label)],
      body: [mainDetails.map(([, value]) => value)],
      theme: 'grid',
      headStyles: {
        fillColor: [29, 43, 78],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'auto',
        fontSize: 10
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 5;
    
    const roomDetails = [
      ['Locali', property.total_rooms || property.bedrooms],
      ['Camere', property.bedrooms],
      ...(showBathrooms ? [['Bagni', property.bathrooms]] : []),
      ['Classe Energetica', property.energy_class || 'N/D'],
    ];
    (doc as any).autoTable({
      startY: yPos,
      head: [roomDetails.map(([label]) => label)],
      body: [roomDetails.map(([, value]) => value)],
      theme: 'grid',
      headStyles: {
        fillColor: [29, 43, 78],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'auto',
        fontSize: 10
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 10;
    
    // Ubicazione
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(29, 43, 78);
    doc.text('UBICAZIONE', 15, yPos);
    
    yPos += 10;
    
    (doc as any).autoTable({
      startY: yPos,
      body: [
        ['Indirizzo:', property.location],
        ['Zona:', property.zones?.name || 'N/D'],
        ['Città:', property.provinces?.name || 'Scandicci']
      ],
      theme: 'plain',
      styles: {
        overflow: 'linebreak',
        cellWidth: 'auto',
        fontSize: 10
      },
      columnStyles: {
        0: {
          fontStyle: 'bold',
          cellWidth: 30
        }
      }
    });
    
    yPos = (doc as any).lastAutoTable.finalY + 10;
    
    // Descrizione
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(29, 43, 78);
    doc.text('DESCRIZIONE', 15, yPos);
    
    yPos += 8;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    
    // Rimuovi i tag HTML dalla descrizione
    const description = property.description?.replace(/<[^>]*>?/gm, '') || 'Nessuna descrizione disponibile';
    
    const splitDescription = doc.splitTextToSize(description, 170);
    doc.text(splitDescription, 15, yPos);
    
    yPos += splitDescription.length * 5 + 10;
    
    // Punti di forza
    if (property.property_strengths && property.property_strengths.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(29, 43, 78);
      doc.text('PUNTI DI FORZA', 15, yPos);
      
      yPos += 8;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      
      property.property_strengths.forEach((strength: any) => {
        doc.text(`• ${strength.description}`, 15, yPos);
        yPos += 5;
      });
      
      yPos += 5;
    }
    
    // Dotazioni
    if (property.property_features && property.property_features.length > 0) {
      // Verifica se c'è abbastanza spazio per le dotazioni, altrimenti aggiungi una nuova pagina
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(29, 43, 78);
      doc.text('DOTAZIONI', 15, yPos);
      
      yPos += 8;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      
      // Raggruppa le dotazioni in colonne
      const features = property.property_features.map(pf => pf.features.name);
      const columns = 2;
      const featuresPerColumn = Math.ceil(features.length / columns);
      
      for (let i = 0; i < featuresPerColumn; i++) {
        for (let j = 0; j < columns; j++) {
          const index = i + j * featuresPerColumn;
          if (index < features.length) {
            const xPos = 15 + (j * 90);
            doc.text(`• ${features[index]}`, xPos, yPos);
          }
        }
        yPos += 5;
      }
    }
    
    // Footer
    doc.setDrawColor(29, 43, 78);
    doc.setLineWidth(0.5);
    doc.line(15, 280, 195, 280);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(29, 43, 78);
    doc.text('MOLINARO IMMOBILIARE', 105, 285, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text('Via Pisana 327, 50018 Scandicci (FI) | Tel: +39 327 8855115 | Email: info@molinaroimmobiliare.it', 105, 289, { align: 'center' });
    doc.text('www.molinaroimmobiliare.it', 105, 292, { align: 'center' });
    
    // Se openInNewTab è true, apri il PDF in una nuova scheda
    if (openInNewTab) {
      // Genera un URL per il PDF
      const pdfBlob = new Blob([doc.output('blob')], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Apri in una nuova scheda
      const newWindow = window.open(pdfUrl, '_blank');
      
      // Pulisci l'URL quando la finestra viene chiusa
      if (newWindow) {
        newWindow.addEventListener('beforeunload', () => {
          URL.revokeObjectURL(pdfUrl);
        });
      }
    } else {
      // Metodo originale: salva il PDF
      doc.save(`${property.reference_number || 'immobile'}-${property.id.substring(0,8)}.pdf`);
    }
    
    return true;
  } catch (error) {
    console.error('Errore nella generazione del PDF:', error);
    throw error;
  }
};

// Funzione per caricare un'immagine da URL
const loadImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Impossibile ottenere il contesto 2D del canvas'));
            return;
          }
          
          ctx.drawImage(img, 0, 0);
          
          try {
            const dataUrl = canvas.toDataURL('image/jpeg');
            resolve(dataUrl);
          } catch (canvasError) {
            console.error('Errore nella conversione del canvas in data URL:', canvasError);
            reject(canvasError);
          }
        } catch (canvasError) {
          console.error('Errore nella creazione del canvas:', canvasError);
          reject(canvasError);
        }
      };
      
      img.onerror = (error) => {
        console.error('Errore nel caricamento dell\'immagine:', error);
        reject(error);
      };
      
      img.src = url;
    } catch (error) {
      console.error('Errore generico nel caricamento dell\'immagine:', error);
      reject(error);
    }
  });
};

export default generatePropertyPDF;
